import React, { useContext, useState, useMemo } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Handle, useReactFlow } from "react-flow-renderer";
import { NodesContext } from "../provider/IvrContext";
import { toast } from 'react-toastify';
import { ROUTES } from '../../../../../utilities/constant';

const ApiNode = ({ data, id }) => {
    const { setNodes } = useReactFlow();
    const { updateNodeData, onRemove, apiWebhookList = [] } = useContext(NodesContext);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [errors, setErrors] = useState({});

    const action = data.action || {};
    const [draftWebhookId, setDraftWebhookId] = useState(action.webhook_id ? String(action.webhook_id) : "");
    const [selectedFixedKeys, setSelectedFixedKeys] = useState(() => new Set(Object.keys(action.selected_fixed_json_keys || {})));
    const [selectedResponseParams, setSelectedResponseParams] = useState(() => new Set(Object.keys(action.selected_response_params || {})));

    const updateNode = (updates) => updateNodeData(id, updates);

    const selectedWebhook = useMemo(
        () => apiWebhookList.find((w) => String(w.id) === draftWebhookId) || null,
        [apiWebhookList, draftWebhookId]
    );

    const fixedJsonEntries = useMemo(() => {
        if (!selectedWebhook?.fixed_json) return [];
        try {
            const parsed = JSON.parse(selectedWebhook.fixed_json);
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                return Object.entries(parsed).map(([key, value]) => ({
                    key,
                    value: typeof value === "object" ? JSON.stringify(value) : String(value),
                }));
            }
        } catch {}
        return [];
    }, [selectedWebhook]);

    const responseParams = useMemo(() => {
        if (!selectedWebhook?.response_parameters) return [];
        try {
            const parsed = JSON.parse(selectedWebhook.response_parameters);
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                return Object.keys(parsed).filter(k => !/^\d+$/.test(k));
            }
            return [];
        } catch {
            return [];
        }
    }, [selectedWebhook]);

    const openModal = () => {
        const act = data.action || {};
        setDraftWebhookId(act.webhook_id ? String(act.webhook_id) : "");
        setSelectedFixedKeys(new Set(Object.keys(act.selected_fixed_json_keys || {})));
        setSelectedResponseParams(new Set(Object.keys(act.selected_response_params || {})));
        setErrors({});
        setModalIsOpen(true);
    };

    const handleClose = () => {
        setErrors({});
        setModalIsOpen(false);
    };

    const handleWebhookChange = (e) => {
        setDraftWebhookId(e.target.value);
        setSelectedFixedKeys(new Set());
        setSelectedResponseParams(new Set());
        setErrors({});
    };

    const toggleFixedKey = (key) => {
        setSelectedFixedKeys((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const toggleResponseParam = (key) => {
        setSelectedResponseParams((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const handleSave = () => {
        if (!draftWebhookId) {
            setErrors({ webhook: "Please select an API webhook" });
            return;
        }

        const fixedJsonObj = {};
        fixedJsonEntries
            .filter(({ key }) => selectedFixedKeys.has(key))
            .forEach(({ key, value }) => { fixedJsonObj[key] = value; });

        const responseParamsObj = {};
        [...selectedResponseParams]
            .filter(key => responseParams.includes(key))
            .forEach((key) => { responseParamsObj[key] = `{${key}}`; });

        updateNode({
            action: {
                webhook_id: selectedWebhook?.id,
                webhook_name: selectedWebhook?.webhook_name || selectedWebhook?.name || "",
                selected_fixed_json_keys: fixedJsonObj,
                selected_response_params: responseParamsObj,
            },
        });

        toast.success("Details updated successfully.");
        setModalIsOpen(false);
    };

    const handleRemoveNode = () => {
        if (data.parent === "gather Node" && data.idToRemove) {
            setNodes((nodes) =>
                nodes.map((node) => {
                    if (!node.data.selectedHandles) return node;
                    const updatedHandles = { ...node.data.selectedHandles };
                    const updatedSmsData = { ...node.data.smsData };
                    delete updatedHandles[data.idToRemove];
                    if (updatedSmsData) {
                        const parts = data.idToRemove.split('-');
                        const handleIndex = parts[parts.length - 1];
                        if (handleIndex) delete updatedSmsData[`DTMF_${handleIndex}`];
                    }
                    return { ...node, data: { ...node.data, selectedHandles: updatedHandles, smsData: updatedSmsData } };
                })
            );
        }
        onRemove(id);
    };

    const configured = !!data.action?.webhook_id;

    const CheckboxSection = ({ title, entries, selectedSet, onToggle, valueLabel }) => (
        <Form.Group className="mb-3">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <Form.Label className="mb-0">{title}</Form.Label>
                <span style={{ fontSize: "12px" }}>
                    <button
                        type="button"
                        onClick={() => entries.forEach(({ key }) => !selectedSet.has(key) && onToggle(key))}
                        style={{ background: "none", border: "none", color: "#17a2b8", cursor: "pointer", padding: "0 6px 0 0", fontSize: "12px" }}
                    >
                        Select All
                    </button>
                    <span style={{ color: "#ccc" }}>|</span>
                    <button
                        type="button"
                        onClick={() => entries.forEach(({ key }) => selectedSet.has(key) && onToggle(key))}
                        style={{ background: "none", border: "none", color: "#6c757d", cursor: "pointer", padding: "0 0 0 6px", fontSize: "12px" }}
                    >
                        Deselect All
                    </button>
                </span>
            </div>
            <div
                style={{
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    maxHeight: "200px",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                    padding: "4px 0",
                }}
            >
                {entries.map(({ key, value }) => (
                    <label
                        key={key}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            cursor: "pointer",
                            padding: "5px 12px",
                            borderRadius: "4px",
                            backgroundColor: selectedSet.has(key) ? "#e8f7f9" : "transparent",
                            margin: 0,
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={selectedSet.has(key)}
                            onChange={() => onToggle(key)}
                            style={{ cursor: "pointer", accentColor: "#17a2b8", width: "15px", height: "15px", flexShrink: 0 }}
                        />
                        <span style={{ fontFamily: "monospace", fontSize: "13px", color: "#212529", flexShrink: 0 }}>{key}</span>
                        <span style={{ fontSize: "12px", color: "#6c757d", marginLeft: "auto", fontFamily: "monospace" }}>
                            {valueLabel ? valueLabel(key, value) : value}
                        </span>
                    </label>
                ))}
            </div>
            <small style={{ color: "#6c757d", marginTop: "4px", display: "block" }}>
                {selectedSet.size} of {entries.length} selected
            </small>
        </Form.Group>
    );

    return (
        <div style={{ position: "relative", width: "65px", height: "40px" }}>
            <div
                className="ivr-flow-node"
                style={{
                    position: "absolute",
                    top: 8,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: "1.5px solid #17a2b8",
                    borderRadius: "10px 10px 0 10px",
                    background: "#fff",
                    zIndex: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "4px",
                    fontSize: "11px",
                }}
            >
                <Handle type="target" position="top" style={{ background: "#17a2b8" }} />
                <Handle type="source" position="bottom" style={{ background: "#17a2b8" }} />

                <span className="ivr-flow-node-label" style={{ fontSize: "10px", fontWeight: "bold", textTransform: "capitalize" }}>
                    {data.label}
                </span>

                <div style={{ display: "flex", gap: "5px", paddingTop: "2px" }}>
                    <i
                        className="fa fa-cog ivr-flow-node-icon"
                        style={{ color: configured ? "#17a2b8" : "#dc3545", cursor: "pointer" }}
                        onClick={openModal}
                    />
                    <i
                        className="fa fa-trash ivr-flow-node-icon"
                        style={{ color: "#17a2b8", cursor: "pointer" }}
                        onClick={handleRemoveNode}
                    />
                </div>
            </div>

            <Modal size="lg" show={modalIsOpen} onHide={handleClose} backdrop={false}>
                <Modal.Header closeButton>
                    <Modal.Title>API Node</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {/* Webhook dropdown */}
                    <Form.Group className="mb-3">
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                            <Form.Label className="mb-0">
                                Select API Webhook <span style={{ color: "red" }}>*</span>
                            </Form.Label>
                            <a
                                href={`/${ROUTES.APIWEBHOOK.CREATE}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: "12px", color: "#17a2b8", textDecoration: "none" }}
                            >
                                + Create API Webhook
                            </a>
                        </div>
                        <Form.Control
                            as="select"
                            value={draftWebhookId}
                            onChange={handleWebhookChange}
                            isInvalid={!!errors.webhook}
                        >
                            <option value="">-- Select Webhook --</option>
                            {apiWebhookList.map((w) => (
                                <option key={w.id} value={w.id}>
                                    {w.webhook_name || w.name}
                                </option>
                            ))}
                        </Form.Control>
                        {errors.webhook && (
                            <Form.Control.Feedback type="invalid">{errors.webhook}</Form.Control.Feedback>
                        )}
                    </Form.Group>

                    {/* Fixed JSON checkboxes */}
                    {fixedJsonEntries.length > 0 && (
                        <CheckboxSection
                            title="Fixed JSON"
                            entries={fixedJsonEntries}
                            selectedSet={selectedFixedKeys}
                            onToggle={toggleFixedKey}
                            valueLabel={(key, value) => value}
                        />
                    )}

                    {/* Response parameter checkboxes */}
                    {responseParams.length > 0 && (
                        <CheckboxSection
                            title="Response Parameters"
                            entries={responseParams.map((key) => ({ key, value: `{${key}}` }))}
                            selectedSet={selectedResponseParams}
                            onToggle={toggleResponseParam}
                        />
                    )}

                    {selectedWebhook && fixedJsonEntries.length === 0 && responseParams.length === 0 && (
                        <p style={{ color: "#6c757d", fontSize: "13px", margin: 0 }}>
                            No fixed JSON or response parameters configured for this webhook.
                        </p>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button onClick={handleSave}>Save</Button>
                    <Button variant="secondary" onClick={handleClose}>Close</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ApiNode;